import { Checkbox } from "@iec-id/ui";
import { useState } from "react";
import { LegalDocumentDialog } from "@/components/legal/legal-document-dialog";
import { useDocumentTitle } from "@/lib/document-title";
import { useOnboardingT } from "../onboarding-i18n";
import { StepTitle } from "../step-title";
import { StepForm, type StepProps } from "./step-shell";

/**
 * Шаг 4 — документы (макет 4b-4).
 *
 * По чекбоксу на документ, а не один общий: принятие фиксируется отдельной
 * записью на каждый ключ с его версией и хешем (`consents.accept`), и склеить
 * три юридически разных согласия в одну галочку — значит потерять возможность
 * доказать, что человек принял именно эту редакцию именно этого документа.
 *
 * Хеш уходит вместе с принятием: если редакция сменится между открытием экрана
 * и нажатием кнопки, сервер отклонит принятие, а не запишет чужой текст.
 *
 * НЕОБЯЗАТЕЛЬНЫХ СОГЛАСИЙ ЗДЕСЬ БОЛЬШЕ НЕТ (решение владельца 23.08.2026): все
 * три переехали на шаг «Уведомления и рассылки», от которого зависят
 * приветственные Баллы. Спрашивать их в двух местах значило сделать условие
 * награды невыполнимым по невнимательности — человек честно включил push, а
 * галочка осталась здесь.
 */
export function DocumentsStep({ state, submit, pending, error, skip }: StepProps) {
	const t = useOnboardingT();
	const documentTitle = useDocumentTitle();
	/** Открытый текст документа. `null` — читалка закрыта. */
	const [reading, setReading] = useState<string | null>(null);

	const required = state.pendingDocuments.filter((document) => document.requiresAcceptance);
	const [accepted, setAccepted] = useState<Record<string, boolean>>({});

	const allAccepted = required.every((document) => accepted[document.key] === true);

	const handleSubmit = () => {
		if (required.length === 0) {
			// Принимать нечего — человек вернулся в мастер, всё уже принято.
			skip();
			return;
		}
		submit({
			step: "documents",
			accepted: required.map((document) => ({
				key: document.key,
				version: document.version,
				sha256: document.sha256,
			})),
		});
	};

	return (
		<>
			<StepTitle tooltip={t("documents.titleTooltip")}>{t("documents.title")}</StepTitle>

			<StepForm
				onSubmit={handleSubmit}
				pending={pending}
				error={error}
				submitLabel={t("documents.submit")}
				disabled={!allAccepted}
			>
				<div className="flex flex-col gap-3.5">
					{required.length === 0 ? (
						<p className="t-body text-fg-muted">{t("documents.empty")}</p>
					) : (
						required.map((document) => (
							<Checkbox
								key={document.key}
								checked={accepted[document.key] === true}
								disabled={pending}
								onCheckedChange={(next) =>
									setAccepted((previous) => ({ ...previous, [document.key]: next }))
								}
								label={
									<span className="t-body text-fg">
										{t("documents.acceptPrefix")}{" "}
										{/* Текст открывается модалкой поверх мастера. Ссылка в новую
										    вкладку уводила человека с шага; `type="button"` и
										    `preventDefault` — чтобы клик по названию не переключал
										    галочку, внутри `<label>` он делал бы именно это. */}
										<button
											type="button"
											className="focus-ring cursor-pointer text-brand-text transition-colors duration-150 ease-smooth hover:text-brand-text-hover"
											onClick={(event) => {
												event.preventDefault();
												setReading(document.key);
											}}
										>
											{documentTitle(document.key, document.title)}
										</button>
									</span>
								}
							/>
						))
					)}
				</div>
			</StepForm>

			{/* Одна модалка на шаг: открытый ключ и решает, что показывать. */}
			{reading ? (
				<LegalDocumentDialog
					documentKey={reading}
					title={documentTitle(
						reading,
						required.find((document) => document.key === reading)?.title ?? reading,
					)}
					open
					onOpenChange={(next) => {
						if (!next) setReading(null);
					}}
				/>
			) : null}
		</>
	);
}
